// A connection to the world (over stdb/worldLink.ts) and everything replicated through it. Call
// update() once per frame: it handles typed events, pings, and advances prediction.
// SDK-generated types and serialization belong only at the transport boundary.
import { clientRequest, type ClientKindInit, type WorldClientMessage, type Snapshot, type WorldServerMessage, type ZoneChange,
  type ItemStack as WireStack } from './events';
import {
  AdminAction, ChatChannel, CombatEventType, ContainerId, DisconnectCode, EntityKind, Facing, GameConstants, HarvestPhase,
  NoticeKind, PartyActionType, QuestActionType, QuestObjectiveKind, QuestStatus, TileEntityType, TradeActionType,
  VendorActionType, ZoneFlags,
} from '../../../shared/src/constants';
import { content, GameContent, useContent } from './content';
import { buffDefinition, LifeState } from '../../../shared/src/rules/buffs';
import { cacheName, decodeBundle } from './contentBundle';
import { DayClock } from '../../../shared/src/dayNight';
import { log } from './log';
import { Chunk, MapTerrain, chunkOfTile, tilesPerChunk } from './map';
import { MapMemory } from './mapMemory';
import { Inventory, consumableCooldownSeconds, emptyStack, isValidItem, itemDef, isConsumable, stack, type ItemStack } from './rules/items';
import type { TradeOffer, TradeView } from './rules/economy';
import { Leveling, progressOf, questStatus } from './rules/progress';
import { TalentRules, talentBonuses, type TalentBonuses } from './rules/talents';
import { TileEntities, tileEntityDef } from '../../../shared/src/rules/world';
import type { QuestDefinition } from './content';
import type { ClientStorage } from './storage';
import { ClientWorld, LocalPlayer, RemoteEntity, ServerClock, type PlayerInput, type SelfState } from './world';
import { scale } from '../../../shared/src/motor';

export enum SessionState { Connecting, LoggingIn, CharacterSelect, InGame, Disconnected }

export interface ChatLine { channel: ChatChannel; from: string; text: string; time: number }
export interface Notice { kind: NoticeKind; text: string; time: number }
export interface CharacterSummary { name: string; appearance: number; lastPlayed: string }
export interface PlayerListEntry { name: string; zoneName: string; appearance: number; inMyParty: boolean; sameMap: boolean }
export interface PartyMemberInfo { name: string; entityId: number; appearance: number; healthPct: number; zoneName: string; online: boolean }
export interface FriendInfo { name: string; online: boolean; zoneName: string; level: number }
export interface PartyPosition { name: string; zoneId: number; instanceId: number; x: number; y: number }
export interface QuestOption { quest: number; status: QuestStatus }
export interface NpcDialogue { npc: number; options: QuestOption[] }
export interface NetStatistics { messagesSent: number; messagesReceived: number; bytesSent: number; bytesReceived: number }

/** What the session tells its screens as it happens. */
export interface SessionEvents {
  joined: () => void;
  zoneEntered: (zoneName: string) => void;
  disconnected: () => void;
  harvestState: (phase: HarvestPhase, x: number, y: number, durationTicks: number) => void;
  questsChanged: (accepted: number, completed: number) => void;
  combatEvent: (type: CombatEventType, entity: number, other: number, value: number) => void;
  chunkChanged: (cx: number, cy: number) => void;
  moneyGained: (copper: number) => void;
  expGained: (exp: number) => void;
}

const maxChatLines = 200;
/** A chat message plus room for a command in front ("/map ..."). */
export const maxChatSend = GameConstants.maxChatLength + 32;
export const collectLostSeconds = 6;

const u8 = (v: number) => Math.min(v >>> 0, 255);
const u16 = (v: number) => Math.min(v >>> 0, 65535);
const int = (v: number) => Math.min(v >>> 0, 0x7fffffff);
const facing = (v: number): Facing => (v & 3) as Facing;
const limit = (text: string, max: number) => (text.length > max ? '' : text);
const readStack = (s: WireStack | undefined): ItemStack => (s ? stack(u16(s.item), int(s.count), s.bound) : emptyStack);
type ClientKind = ClientKindInit;

const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export enum Delivery {
  ReliableOrdered = 0,
  /** Only useful while fresh (inputs): a transport may drop it when backed up, since newer ones follow. */
  Sequenced = 1,
}

/** Called whenever world messages arrive (the page keeps going on it while the browser holds back its frames). */
export const arrivals: { onArrival: (() => void) | null } = { onArrival: null };

/**
 * What carries typed requests/events: the SpacetimeDB adapter translates generated native
 * types at this boundary. The session never serializes an in-process message.
 */
export interface WorldSocket {
  readonly isClosed: boolean;
  /** Serialized payload byte counters, if the transport can report them (not synthetic event sizes). */
  readonly bytesReceived?: number;
  readonly bytesSent?: number;
  tryReceive(): WorldServerMessage | null | undefined;
  send(request: WorldClientMessage, delivery?: Delivery): void;
  close(reason?: string): void;
  abort(reason?: string): void;
}

export class ClientSession {
  state = SessionState.Connecting;
  readonly address: string;
  readonly world = new ClientWorld();
  readonly local = new LocalPlayer();
  readonly clock = new ServerClock();
  readonly inventory = new Inventory();
  readonly statistics: NetStatistics = { messagesSent: 0, messagesReceived: 0, bytesSent: 0, bytesReceived: 0 };

  characters: CharacterSummary[] = [];
  maxCharacters = 5;
  serverId = '';
  serverName = '';
  maxPlayers = 0;
  /** The last create/select error (cleared when a new request is sent). */
  characterError: string | null = null;
  players: PlayerListEntry[] = [];
  readonly chat: ChatLine[] = [];
  readonly notices: Notice[] = [];
  health = 100;
  maxHealth = 100;
  /** Mana (staves spend it): the server's, less what attacks sent since spent (spendMana). */
  mana = 0;
  maxMana = 0;
  isAdmin = false;
  godMode = false;
  /** The maps an admin can teleport to (key, name). */
  adminMaps: { key: string; name: string }[] = [];
  level = 1;
  /** Exp within the current level. */
  exp = 0;
  /** Allocated talent nodes (the Start is free and never listed). */
  talents: number[] = [];
  /** Aura nodes from party members nearby that reach us. */
  auras: number[] = [];
  /** Healing circles in this place, by id: where, which spell scroll, and the server ticks they last between. */
  readonly healZones = new Map<number, { x: number; y: number; item: number; startTick: number; endTick: number }>();
  /** Players' corpses in the instance, by their entity id. */
  readonly corpses = new Map<number, { x: number; y: number; appearance: number; facing: number }>();
  /** Buffs and debuffs on players in the instance, by row id. */
  readonly buffs = new Map<number, { entityId: number; kind: number; startTick: number; endTick: number }>();
  /** Where this player stands through death (shared/src/rules/buffs.ts LifeState) and, as a ghost, where its corpse lies. */
  life = { state: LifeState.Alive as LifeState, corpseMap: '', corpseX: 0, corpseY: 0 };
  /** Copper. */
  money = 0;
  partyMembers: PartyMemberInfo[] = [];
  partyLeader = '';
  partyMaxSize = 5;
  /** Latest known position of each party member, by lower-case name. */
  readonly partyPositions = new Map<string, { pos: PartyPosition; time: number }>();
  pendingInvite: { from: string; expires: number } | null = null;
  pendingTrade: { from: string; expires: number } | null = null;
  trade: TradeView | null = null;
  tradeChangedAt = 0;
  /** Names we ignore (the server filters them). */
  ignored: string[] = [];
  /** Our friends as the server last sent them (online first, then by name). */
  friends: FriendInfo[] = [];
  /** Active quests → stored objective progress (Collect objectives read the bag instead). */
  readonly activeQuests = new Map<number, number[]>();
  readonly completedQuests = new Set<number>();
  /** "Bring" objectives whose progress just fell, by "quest:objective": when, and how many were lost. */
  readonly collectLost = new Map<string, { at: number; lost: number }>();
  dialogue: NpcDialogue | null = null;
  /** When the next consumable can be used (session time). */
  consumableReadyAt = 0;
  disconnectCode = DisconnectCode.Unknown;
  disconnectMessage = '';
  mapMemory = new MapMemory();
  /**
   * Bumped whenever something a window might show changes (anything but snapshots, chunks and pings) and by local
   * predictions; windows that rebuild on change compare it with the one they drew.
   */
  revision = 0;

  private readonly listeners: { [K in keyof SessionEvents]?: SessionEvents[K][] } = {};
  private socket: WorldSocket | null = null;
  private goodbye: { code: DisconnectCode; message: string } | null = null;
  private nextPingAt = 0;
  private roundTripMs = 0;
  private timeNow = 0;
  /** The time of day the world told us, run on locally (only lighting uses it). */
  private readonly dayClock = new DayClock();
  private haveSnapshot = false;
  private lastSnapshotTick = 0;
  private pendingSelf: { lastProcessedInput: number; self: SelfState } | null = null;
  private haveStats = false;
  private haveQuestLog = false;
  /** Messages are held while content is being put in effect (what follows may depend on it). */
  private busy: Promise<void> | null = null;
  private readonly mapMemories = new Map<string, { memory: MapMemory; saved: number }>();
  private terrain: MapTerrain | null = null;
  private terrainHash = 0n;
  private cacheTerrain = false;
  private pendingChunks: { cx: number; cy: number; changes: [number, number][] }[] = [];

  private constructor(address: string, private readonly storage: ClientStorage, private readonly account: string) {
    this.address = address;
  }

  /** The account's name, as the page knows it (from its sign-in, or "guest"). */
  get username(): string { return this.account; }
  get time(): number { return this.timeNow; }
  /** The time of day now (a fraction of a day; see DayNight). */
  get timeOfDay(): number { return this.dayClock.timeAt(this.timeNow); }
  /** Round trip to the world in milliseconds. */
  get ping(): number { return this.roundTripMs; }
  get inParty(): boolean { return this.partyMembers.length > 0; }
  get isPartyLeader(): boolean { return this.inParty && this.partyLeader.toLowerCase() === this.local.name.toLowerCase(); }
  get unspentTalentPoints(): number { return TalentRules.unspent(this.level, this.talents); }
  /** What our talents (and the auras on us) add up to. */
  get talentBonuses(): TalentBonuses { return talentBonuses(content(), this.talents, this.auras); }
  isInMyParty(name: string): boolean { return this.partyMembers.some(m => m.name.toLowerCase() === name.toLowerCase()); }
  isIgnored(name: string): boolean { return this.ignored.some(n => n.toLowerCase() === name.toLowerCase()); }
  questStatus(q: QuestDefinition): QuestStatus {
    return questStatus(q, id => this.completedQuests.has(id), this.activeQuests.get(q.id) ?? null, this.inventory);
  }

  /**
   * Joins the world over its transport (the SpacetimeDB world: its identity is the account). The first message says
   * who we are; the world answers with its hello and our characters.
   */
  static over(opened: Promise<WorldSocket>, address: string, account: string, storage: ClientStorage): ClientSession {
    const session = new ClientSession(address, storage, account);
    log.info(`Connecting to the world at ${address}`);
    opened.then(socket => {
      if (session.state === SessionState.Disconnected) { socket.abort('gave up'); return; }
      session.socket = socket;
      session.send({
        case: 'connect',
        value: { key: GameConstants.connectionKey, protocol: GameConstants.protocolVersion, contentHash: content().fingerprint },
      });
    }, e => session.fail(DisconnectCode.Unknown, (e as Error).message || `Could not reach the world at ${address}.`));
    return session;
  }

  on<K extends keyof SessionEvents>(event: K, handler: SessionEvents[K]): void {
    ((this.listeners[event] ??= []) as SessionEvents[K][]).push(handler);
  }

  private emit<K extends keyof SessionEvents>(event: K, ...args: Parameters<SessionEvents[K]>): void {
    for (const h of this.listeners[event] ?? []) (h as (...a: Parameters<SessionEvents[K]>) => void)(...args);
  }

  /** Marks window-visible state as changed. */
  touch(): void { this.revision++; }

  // =================================================================================================================
  // per frame

  /** Pumps the network and advances prediction. dt in seconds; buttons: InputButtons. */
  update(dt: number, buttons: number): void {
    this.timeNow += dt;
    this.pollNetwork();
    if (this.state !== SessionState.InGame) return;
    this.clock.advance(dt);
    if (this.pendingSelf) {
      const { lastProcessedInput, self } = this.pendingSelf;
      this.pendingSelf = null;
      this.local.reconcile(lastProcessedInput, self, this.world.map);
    }
    this.local.update(dt, buttons, this.world.map, inputs => this.sendInputs(inputs));
    const renderTick = this.clock.renderTick;
    for (const e of this.world.entities.values()) e.update(renderTick, dt);
  }

  private pollNetwork(): void {
    const socket = this.socket;
    if (!socket) return;
    while (!this.busy) {
      const m = socket.tryReceive();
      if (!m) break;
      this.statistics.messagesReceived++;
      this.statistics.bytesReceived = socket.bytesReceived ?? 0;
      try { this.onReceive(m); }
      catch (e) { log.error(`Malformed ${m.kind.case} from the server`, e); }
    }
    if (this.busy) return;
    if (socket.isClosed) {
      this.socket = null;
      const g = this.goodbye ?? {
        code: DisconnectCode.Unknown,
        message: this.state === SessionState.Connecting ? 'The server rejected the connection.' : 'Lost the connection to the server.',
      };
      this.fail(g.code, g.message);
      return;
    }
    if (this.timeNow >= this.nextPingAt) {
      this.nextPingAt = this.timeNow + 1;
      this.send({ case: 'ping', value: { token: Math.floor(nowMs()) >>> 0, roundTripMs: this.roundTripMs } });
    }
  }

  private send(kind: ClientKind, delivery = Delivery.ReliableOrdered): void {
    if (!this.socket) return;
    this.statistics.messagesSent++;
    this.socket.send(clientRequest(kind), delivery);
    this.statistics.bytesSent = this.socket.bytesSent ?? 0;
  }

  private sendInGame(kind: ClientKind): void {
    if (this.state === SessionState.InGame) this.send(kind);
  }

  private sendInputs(inputs: readonly PlayerInput[]): void {
    const count = Math.min(inputs.length, GameConstants.inputRedundancy);
    const buttons = new Uint8Array(count);
    for (let i = 0; i < count; i++) buttons[i] = inputs[inputs.length - 1 - i].buttons; // newest first
    this.send({ case: 'input', value: { epoch: this.world.epoch, newestSequence: count > 0 ? inputs[inputs.length - 1].sequence : 0, buttons } },
      Delivery.Sequenced);
  }

  // =================================================================================================================
  // requests

  sendChat(text: string): void {
    text = text.trim();
    if (text.length === 0) return;
    this.sendInGame({ case: 'chatSend', value: { text: text.slice(0, maxChatSend) } });
  }

  sendInventoryMove(fromC: ContainerId, from: number, toC: ContainerId, to: number): void {
    if (this.state !== SessionState.InGame) return;
    this.inventory.tryMove(fromC, from, toC, to); // predicted; the server's sync decides
    this.revision++;
    this.send({ case: 'inventoryMove', value: { fromContainer: fromC, fromIndex: Math.max(0, from), toContainer: toC, toIndex: Math.max(0, to) } });
  }

  sendInventoryQuickMove(c: ContainerId, index: number): void {
    if (this.state !== SessionState.InGame) return;
    this.inventory.quickMove(c, index);
    this.revision++;
    this.send({ case: 'inventoryQuickMove', value: { container: c, index: Math.max(0, index) } });
  }

  sendDestroyItem(c: ContainerId, index: number, item: number): void {
    this.sendInGame({ case: 'destroyItem', value: { container: c, index: Math.max(0, index), item } });
  }

  /** Drinks a potion. Returns whether it was sent: checks health and the cooldown first. */
  tryUseItem(c: ContainerId, index: number): boolean {
    if (this.state !== SessionState.InGame) return false;
    const s = this.inventory.get(c, index);
    const def = itemDef(s.item);
    if (!def || !isConsumable(def)) return false;
    const error = this.timeNow < this.consumableReadyAt
      ? `Wait ${(Math.round((this.consumableReadyAt - this.timeNow) * 10) / 10).toString()}s before using another.`
      : this.health >= this.maxHealth ? "You're already at full health." : null;
    if (error) {
      this.addNotice(NoticeKind.Error, error);
      return false;
    }
    this.consumableReadyAt = this.timeNow + consumableCooldownSeconds;
    this.send({ case: 'useItem', value: { container: c, index: Math.max(0, index), item: s.item } });
    return true;
  }

  sendVendorBuy(npc: number, index: number, item: number, count: number): void {
    this.sendVendor(npc, VendorActionType.Buy, ContainerId.Main, index, item, count);
  }
  sendVendorSell(npc: number, c: ContainerId, index: number, item: number): void {
    this.sendVendor(npc, VendorActionType.Sell, c, index, item, 0);
  }
  sendVendorSellJunk(npc: number): void { this.sendVendor(npc, VendorActionType.SellJunk, ContainerId.Main, 0, 0, 0); }

  private sendVendor(npc: number, action: VendorActionType, c: ContainerId, index: number, item: number, count: number): void {
    this.sendInGame({
      case: 'vendorAction',
      value: { epoch: this.world.epoch, npc, action, container: c, index: Math.max(0, index), item, count: Math.min(Math.max(count, 0), 255) },
    });
  }

  sendDrop(c: ContainerId, index: number, count: number): void {
    this.sendInGame({ case: 'dropItem', value: { container: c, index: Math.max(0, index), count: Math.max(0, count) } });
  }
  sendBagTake(bag: number, slot: number, expected: number): void {
    this.sendInGame({ case: 'bagTake', value: { bag, slot: Math.max(0, slot), expectedItem: expected } });
  }
  sendBagTakeAll(bag: number): void { this.sendInGame({ case: 'bagTakeAll', value: { bag } }); }
  sendBagDeposit(bag: number, c: ContainerId, index: number): void {
    this.sendInGame({ case: 'bagDeposit', value: { bag, container: c, index: Math.max(0, index) } });
  }

  sendCreateCharacter(name: string, appearance: number): void {
    if (this.state !== SessionState.CharacterSelect) return;
    this.characterError = null;
    this.send({ case: 'createCharacter', value: { name, appearance } });
  }

  sendSelectCharacter(name: string): void {
    if (this.state !== SessionState.CharacterSelect) return;
    this.characterError = null;
    this.send({ case: 'selectCharacter', value: { name } });
  }

  sendIgnore(name: string, ignore: boolean): void { this.sendInGame({ case: 'ignoreAction', value: { ignore, name } }); }
  sendFriend(name: string, add: boolean): void { this.sendInGame({ case: 'friendAction', value: { add, name } }); }

  sendTradeRequest(name: string): void { this.sendInGame({ case: 'tradeAction', value: { action: TradeActionType.Request, name } }); }

  /** Answers the pending trade request. */
  sendTradeReply(accept: boolean): void {
    this.pendingTrade = null;
    this.sendInGame({ case: 'tradeAction', value: { action: accept ? TradeActionType.AcceptRequest : TradeActionType.DeclineRequest } });
  }

  sendTradeOffer(offer: TradeOffer): void {
    this.sendInGame({
      case: 'tradeAction',
      value: { action: TradeActionType.Offer, offer: { slots: offer.slots.map(s => ({ ...s, slot: Math.max(0, s.slot) })), copper: BigInt(Math.max(0, Math.floor(offer.copper))) } },
    });
  }

  /** Accepts the trade exactly as we last saw it. */
  sendTradeAccept(): void {
    if (this.trade) this.sendInGame({ case: 'tradeAction', value: { action: TradeActionType.Confirm, revision: this.trade.revision } });
  }

  sendTradeCancel(): void { this.sendInGame({ case: 'tradeAction', value: { action: TradeActionType.Cancel } }); }

  sendPartyAction(action: PartyActionType, name = ''): void {
    if (this.state !== SessionState.InGame) return;
    if (action === PartyActionType.Accept || action === PartyActionType.Decline) this.pendingInvite = null;
    this.send({ case: 'partyAction', value: { action, name } });
  }

  sendAdmin(action: AdminAction, arg = 0, count = 0, x = 0, y = 0, name = ''): void {
    this.sendInGame({ case: 'adminCommand', value: { action, arg, count, x, y, name: name.slice(0, 64) } });
  }

  sendBlock(on: boolean): void { this.sendInGame({ case: 'block', value: { epoch: this.world.epoch, on } }); }

  sendAttack(angle: number, distance: number): void {
    this.sendInGame({ case: 'attack', value: { epoch: this.world.epoch, angle, viewTick: Math.max(0, Math.floor(this.clock.renderTick)), distance } });
  }

  sendCraft(recipe: number, count: number, station: number): void {
    this.sendInGame({ case: 'craft', value: { recipe, count: Math.max(0, count), station } });
  }

  sendTalentAction(node: number, refund: boolean): void { this.sendInGame({ case: 'talentAction', value: { node, refund } }); }
  sendGatherEntity(placementId: string): void { this.sendInGame({ case: 'gatherEntity', value: { epoch: this.world.epoch, placementId } }); }
  sendUsePortal(tileEntity: number): void { this.sendInGame({ case: 'usePortal', value: { epoch: this.world.epoch, tileEntity } }); }
  sendTalkNpc(tileEntity: number): void { this.sendInGame({ case: 'talkNpc', value: { epoch: this.world.epoch, tileEntity } }); }
  sendQuestAction(quest: number, action: QuestActionType): void { this.sendInGame({ case: 'questAction', value: { quest, action } }); }
  sendInteractStart(x: number, y: number): void { this.sendInGame({ case: 'interactStart', value: { epoch: this.world.epoch, x, y } }); }
  sendInteractCancel(): void { this.sendInGame({ case: 'interactCancel', value: { epoch: this.world.epoch } }); }
  sendReleaseCorpse(): void { this.sendInGame({ case: 'releaseCorpse', value: { epoch: this.world.epoch } }); }
  sendRekindle(): void { this.sendInGame({ case: 'rekindle', value: { epoch: this.world.epoch } }); }

  addLocalMessage(channel: ChatChannel, text: string): void { this.addChat(channel, '', text); }

  private addChat(channel: ChatChannel, from: string, text: string): void {
    this.chat.push({ channel, from, text, time: this.timeNow });
    if (this.chat.length > maxChatLines) this.chat.splice(0, this.chat.length - maxChatLines);
  }

  private addNotice(kind: NoticeKind, text: string): void {
    this.notices.push({ kind, text, time: this.timeNow });
    if (this.notices.length > 16) this.notices.shift();
  }

  // =================================================================================================================
  // messages

  private onReceive(m: WorldServerMessage): void {
    const k = m.kind;
    if (k.case !== 'snapshot' && k.case !== 'pong' && k.case !== 'chunkData' && k.case !== 'chunkUnload') this.revision++;
    const epoch = this.world.epoch;
    switch (k.case) {
      case 'pong':
        this.roundTripMs = Math.max(0, ((Math.floor(nowMs()) >>> 0) - k.value.token) >>> 0);
        if (this.roundTripMs > 60_000) this.roundTripMs = 0;
        break;
      case 'disconnect':
        this.goodbye = { code: u8(k.value.code), message: limit(k.value.message, 256) };
        break;
      case 'serverHello':
        this.serverId = limit(k.value.serverId, 64);
        this.serverName = limit(k.value.serverName, 64);
        this.maxCharacters = u8(k.value.maxCharacters);
        this.state = SessionState.LoggingIn;
        break;
      case 'contentData': {
        // The world's content differs from ours: use it, and keep it for next time. It arrives ahead of the character
        // list; what follows waits until it's in effect.
        const bundle = k.value.bundle;
        this.busy = decodeBundle(bundle).then(files => {
          const c = new GameContent(files);
          for (const p of c.problems) log.warn(`Content from the world: ${p}`);
          useContent(c);
          this.storage.write(cacheName, bundle);
          log.info(`Using the world's content (${c.items.size} items, ${c.tiles.tiles.length} tiles, fingerprint ${c.fingerprint.toString(16).padStart(8, '0')})`);
        }, e => log.error('Could not read the content the world sent', e)).finally(() => { this.busy = null; });
        break;
      }
      case 'characterList':
        this.characters = k.value.characters.map(c => ({ name: limit(c.name, 16), appearance: u8(c.appearance), lastPlayed: limit(c.lastPlayed, 64) }));
        if (this.state === SessionState.Connecting || this.state === SessionState.LoggingIn) this.state = SessionState.CharacterSelect;
        break;
      case 'characterResult':
        this.characterError = k.value.error ? limit(k.value.error, 200) : null;
        break;
      case 'welcome': {
        const w = k.value;
        this.local.entityId = u16(w.entityId);
        this.local.name = limit(w.characterName, 16);
        this.local.appearance = u8(w.appearance);
        this.serverName = limit(w.serverName, 64);
        this.maxPlayers = u16(w.maxPlayers);
        if (w.tickRate !== GameConstants.tickRate) log.warn(`Server tick rate ${w.tickRate} differs from the client's ${GameConstants.tickRate}`);
        log.info(`Welcomed by '${this.serverName}' as entity ${this.local.entityId}`);
        break;
      }
      case 'zoneChange': this.handleZoneChange(k.value); break;
      case 'mapData':
        if (u8(k.value.epoch) === epoch && !this.terrain) void this.useTerrain(k.value.terrain, epoch, true);
        break;
      case 'chunkData': {
        const c = k.value;
        if (u8(c.epoch) !== epoch) break;
        if (c.indices.length !== c.objects.length || c.indices.length > tilesPerChunk) throw new Error("chunk changes don't pair up");
        const changes: [number, number][] = c.indices.map((index, i) => {
          if (index >= tilesPerChunk) throw new Error('chunk change outside the chunk');
          return [index, u16(c.objects[i])];
        });
        if (this.terrain) this.applyChunk(c.x, c.y, changes);
        else this.pendingChunks.push({ cx: c.x, cy: c.y, changes });
        break;
      }
      case 'chunkUnload': {
        const c = k.value;
        if (u8(c.epoch) !== epoch) break;
        this.world.map.remove(c.x, c.y);
        this.world.removeInChunk(c.x, c.y);
        this.emit('chunkChanged', c.x, c.y);
        break;
      }
      case 'tileUpdate': {
        const t = k.value;
        if (u8(t.epoch) !== epoch) break;
        this.world.map.setTile(t.x, t.y, u16(t.ground), u16(t.object));
        this.mapMemory.recordTile(t.x, t.y, u16(t.ground), u16(t.object));
        this.emit('chunkChanged', chunkOfTile(t.x), chunkOfTile(t.y));
        break;
      }
      case 'entitySpawn': {
        const p = k.value;
        const id = u16(p.entityId);
        if (u8(p.epoch) !== epoch || id === this.local.entityId) break;
        const tick = this.haveSnapshot ? this.lastSnapshotTick : Math.max(0, Math.floor(this.clock.estimatedTick));
        const e = new RemoteEntity(id, u8(p.kind) as EntityKind, limit(p.name, 32), u8(p.appearance), p.x / scale, p.y / scale,
          facing(p.facing), u8(p.flags), tick);
        e.level = Math.max(1, u8(p.level));
        e.armor = p.armor.map(u16);
        this.world.entities.set(id, e);
        break;
      }
      case 'entityDespawn':
        if (u8(k.value.epoch) === epoch) this.world.entities.delete(u16(k.value.entityId));
        break;
      case 'entityEquipment': {
        const e = this.world.entities.get(u16(k.value.entityId));
        if (u8(k.value.epoch) === epoch && e) e.armor = k.value.armor.map(u16);
        break;
      }
      case 'snapshot': this.handleSnapshot(k.value); break;
      case 'combatEvent': {
        const c = k.value;
        if (u8(c.epoch) === epoch) this.emit('combatEvent', u8(c.type), u16(c.entity), u16(c.other), c.value);
        break;
      }
      case 'playerStats': {
        const st = k.value;
        const health = u16(st.health), maxHealth = Math.max(1, u16(st.maxHealth)), level = u8(st.level), exp = int(st.exp);
        const money = Math.max(0, Number(st.money));
        let gained = 0;
        if (level === this.level) gained = exp - this.exp;
        else if (level > this.level) {
          gained = Leveling.expToNext(this.level) - this.exp + exp;
          for (let l = this.level + 1; l < level; l++) gained += Leveling.expToNext(l);
        }
        if (this.haveStats && gained > 0) this.emit('expGained', gained);
        if (this.haveStats && money > this.money) this.emit('moneyGained', money - this.money);
        this.health = health;
        this.maxHealth = maxHealth;
        this.mana = u16(st.mana);
        this.maxMana = u16(st.maxMana);
        this.money = money;
        this.haveStats = true;
        this.level = level;
        this.exp = exp;
        break;
      }
      case 'lifeState': {
        const l = k.value, state = u8(l.state);
        this.life = { state: state <= LifeState.Ghost ? state : LifeState.Alive, corpseMap: limit(l.corpseMap, 64),
          corpseX: Number.isFinite(l.corpseX) ? l.corpseX : 0, corpseY: Number.isFinite(l.corpseY) ? l.corpseY : 0 };
        this.touch();
        break;
      }
      case 'corpseState': {
        const c = k.value;
        if (u8(c.epoch) !== epoch) break;
        if (c.removed) this.corpses.delete(c.entityId);
        else if (this.corpses.size < 256 && Number.isFinite(c.x) && Number.isFinite(c.y))
          this.corpses.set(c.entityId, { x: c.x, y: c.y, appearance: u8(c.appearance), facing: u8(c.facing) & 3 });
        break;
      }
      case 'buffState': {
        const b = k.value;
        if (u8(b.epoch) !== epoch) break;
        if (b.removed) this.buffs.delete(b.id);
        else if (this.buffs.size < 1024 && buffDefinition(b.kind))
          this.buffs.set(b.id, { entityId: b.entityId, kind: b.kind, startTick: b.startTick >>> 0, endTick: b.endTick >>> 0 });
        this.touch();
        break;
      }
      case 'healZoneState': {
        const z = k.value;
        if (u8(z.epoch) !== epoch) break;
        if (z.removed) this.healZones.delete(z.id);
        else if (content().item(z.item)?.spell?.zone && this.healZones.size < 256)
          this.healZones.set(z.id, { x: z.x, y: z.y, item: z.item, startTick: z.startTick >>> 0, endTick: z.endTick >>> 0 });
        break;
      }
      case 'talents': {
        const t = k.value, known = (id: number) => content().talent(id) !== undefined;
        this.talents = t.allocated.slice(0, 256).filter(known);
        this.auras = t.auras.slice(0, 256).filter(known);
        this.touch();
        break;
      }
      case 'inventorySync': {
        const before = this.collectProgress();
        const s = k.value;
        const put = (c: ContainerId, list: WireStack[]) => {
          const arr = this.inventory.container(c)!;
          for (let i = 0; i < arr.length; i++) this.inventory.set(c, i, readStack(list[i]));
        };
        put(ContainerId.Main, s.main);
        put(ContainerId.Belt, s.belt);
        put(ContainerId.Armor, s.armor);
        this.noteCollectLoss(before);
        break;
      }
      case 'harvestState': {
        const h = k.value;
        if (u8(h.epoch) === epoch) this.emit('harvestState', u8(h.phase), h.x, h.y, u16(h.durationTicks));
        break;
      }
      case 'placedEntityState': {
        const e = k.value;
        if (u8(e.epoch) !== epoch) break;
        if (e.removed) this.world.map.placedEntities.delete(e.id);
        else if (content().entity(e.definition) && this.world.map.inBounds(e.x, e.y) && e.id.length <= 40)
          this.world.map.placedEntities.set(e.id, { id: e.id, definition: e.definition, x: e.x, y: e.y,
            offsetX: e.offsetX, offsetY: e.offsetY, depleted: e.depleted });
        this.touch();
        break;
      }
      case 'tileEntitySpawn': {
        const t = k.value;
        if (u8(t.epoch) !== epoch || !tileEntityDef(u8(t.type))) break;
        this.world.addTileEntity({ id: t.id, type: u8(t.type) as TileEntityType, x: t.x, y: t.y, data: t.data, label: limit(t.label, TileEntities.maxLabelLength) });
        break;
      }
      case 'tileEntityDespawn':
        if (u8(k.value.epoch) === epoch) this.world.removeTileEntity(k.value.id);
        break;
      case 'lootBagSpawn': {
        const b = k.value;
        if (u8(b.epoch) !== epoch) break;
        this.world.bags.set(b.id, { id: b.id, x: b.x, y: b.y, contents: b.contents.slice(0, 100).map(readStack).filter(s => isValidItem(s.item)) });
        break;
      }
      case 'lootBagDespawn':
        if (u8(k.value.epoch) === epoch) this.world.bags.delete(k.value.id);
        break;
      case 'notice': this.addNotice(u8(k.value.kind), limit(k.value.text, 128)); break;
      case 'chat': this.addChat(u8(k.value.channel), limit(k.value.from, 32), limit(k.value.text, 512)); break;
      case 'playerList':
        this.maxPlayers = u16(k.value.maxPlayers);
        this.players = k.value.entries.map(e => ({ name: limit(e.name, 16), zoneName: limit(e.zoneName, 64), appearance: u8(e.appearance), inMyParty: e.inMyParty, sameMap: e.sameMap }));
        break;
      case 'adminStatus':
        this.isAdmin = k.value.isAdmin;
        this.godMode = k.value.godMode;
        this.adminMaps = k.value.isAdmin ? k.value.maps.slice(0, 256).map(m => ({ key: limit(m.key, 64), name: limit(m.name, 64) })) : [];
        this.touch();
        break;
      case 'partyState':
        this.partyLeader = limit(k.value.leader, 16);
        this.partyMaxSize = u8(k.value.maxSize);
        this.partyMembers = k.value.members.map(p => ({
          name: limit(p.name, 16), entityId: u16(p.entityId), appearance: u8(p.appearance), healthPct: u8(p.healthPct),
          zoneName: limit(p.zoneName, 64), online: p.online,
        }));
        if (this.inParty) this.pendingInvite = null;
        break;
      case 'partyInvite':
        this.pendingInvite = { from: limit(k.value.inviter, 16), expires: this.timeNow + k.value.expiresIn };
        break;
      case 'partyPositions':
        this.partyPositions.clear();
        for (const p of k.value.members) {
          const pos = { name: limit(p.name, 16), zoneId: u16(p.zoneId), instanceId: p.instanceId, x: p.x, y: p.y };
          this.partyPositions.set(pos.name.toLowerCase(), { pos, time: this.timeNow });
        }
        break;
      case 'timeOfDay': this.dayClock.set(k.value.time, k.value.daySeconds, this.timeNow); break;
      case 'ignoreList': this.ignored = k.value.names.map(n => limit(n, 16)); break;
      case 'friendList':
        this.friends = k.value.friends.slice(0, 255).map(f => ({ name: limit(f.name, 16), online: f.online, zoneName: limit(f.zoneName, 64), level: u8(f.level) }));
        break;
      case 'npcDialogue':
        this.dialogue = { npc: u16(k.value.npc), options: k.value.options.map(o => ({ quest: u16(o.quest), status: u8(o.status) as QuestStatus })) };
        break;
      case 'questLog': {
        const active = k.value.active.map(q => ({ quest: u16(q.quest), progress: q.progress.map(int) }));
        const completed = k.value.completed.map(u16);
        const accepted = active.filter(q => !this.activeQuests.has(q.quest) && !this.completedQuests.has(q.quest)).length;
        const finished = completed.filter(q => !this.completedQuests.has(q)).length;
        this.activeQuests.clear();
        for (const q of active) if (content().quest(q.quest)) this.activeQuests.set(q.quest, q.progress);
        this.completedQuests.clear();
        for (const q of completed) this.completedQuests.add(q);
        if (this.haveQuestLog && (accepted > 0 || finished > 0)) this.emit('questsChanged', accepted, finished);
        this.haveQuestLog = true;
        break;
      }
      case 'tradeInvite':
        this.pendingTrade = { from: limit(k.value.from, 16), expires: this.timeNow + k.value.expiresIn };
        break;
      case 'tradeState': {
        const v = k.value.view;
        const view: TradeView | null = v ? {
          partner: limit(v.partner, 16), revision: v.revision,
          mine: { slots: (v.mine?.slots ?? []).map(s => ({ slot: u8(s.slot), item: u16(s.item), count: int(s.count) })), copper: Number(v.mine?.copper ?? 0n) },
          mineAccepted: v.mineAccepted, theirs: v.theirs.map(readStack), theirCopper: Number(v.theirCopper), theirsAccepted: v.theirsAccepted,
        } : null;
        if (!view || !this.trade || view.revision !== this.trade.revision) this.tradeChangedAt = this.timeNow;
        this.trade = view;
        if (view) this.pendingTrade = null;
        break;
      }
      default:
        log.warn('Unexpected event from the server');
    }
  }

  /** The start map keeps the file name it had before there were several maps; others are told apart by name. */
  private mapFileName(zoneId: number, terrainHash: bigint): string {
    // Legacy filenames had no terrain identity, so they cannot safely be migrated after a map edit/replacement.
    return `maps/${this.serverId || 'local'}_${this.local.name.toLowerCase()}_zone-${zoneId}_${terrainHash.toString(16)}.map`;
  }

  /** Saves explored maps that changed since the last save (cheap to call often). */
  saveMapMemory(): void {
    if (!this.local.name) return;
    for (const [file, entry] of this.mapMemories) {
      if (entry.memory.version === entry.saved) continue;
      entry.saved = entry.memory.version;
      entry.memory.toBytes().then(bytes => this.storage.write(file, bytes), e => log.warn(`Could not save ${file}: ${(e as Error).message}`));
    }
  }

  private handleZoneChange(z: ZoneChange): void {
    this.dayClock.set(z.time?.time ?? 0.5, z.time?.daySeconds ?? 0, this.timeNow);
    // Exploration of hand-made maps persists; each dungeon instance gets a fresh, throwaway map.
    this.saveMapMemory();
    this.healZones.clear(); // the link sends the new place's
    this.corpses.clear();
    this.buffs.clear();
    const flags = u8(z.flags) as ZoneFlags;
    const zoneName = limit(z.zoneName, 64);
    if ((flags & ZoneFlags.Instanced) === 0) {
      const file = this.mapFileName(u16(z.zoneId), z.terrainHash);
      let entry = this.mapMemories.get(file);
      if (!entry) {
        entry = { memory: new MapMemory(), saved: -1 };
        this.mapMemories.set(file, entry);
        void entry.memory.load(this.storage.read(file), file);
      }
      this.mapMemory = entry.memory;
    } else {
      this.mapMemory = new MapMemory();
    }
    this.mapMemory.widthTiles = int(z.widthTiles);
    this.mapMemory.heightTiles = int(z.heightTiles);
    const epoch = u8(z.epoch);
    this.world.enterZone({
      epoch, zoneId: u16(z.zoneId), instanceId: z.instanceId, zoneName, widthTiles: int(z.widthTiles), heightTiles: int(z.heightTiles), flags,
      lights: (z.lights ?? []).slice(0, 4096).map(l => ({ x: int(l.x), y: int(l.y), radius: Math.min(255, int(l.radius)), color: l.color & 0xffffff, flicker: !!l.flicker })),
    });
    this.local.reset(z.x, z.y, facing(z.facing));
    this.clock.reset();
    this.clock.onServerTick(z.serverTick);
    this.haveSnapshot = false;
    this.pendingSelf = null;
    const first = this.state !== SessionState.InGame;
    this.state = SessionState.InGame;
    log.info(`Entered ${zoneName} (instance ${z.instanceId}, epoch ${epoch})`);
    if (first) this.emit('joined');
    this.emit('zoneEntered', zoneName);

    // The zone's terrain: ours if we kept it (by its hash), otherwise ask for it once. Chunks wait for it.
    this.terrain = null;
    this.pendingChunks = [];
    this.terrainHash = z.terrainHash;
    this.cacheTerrain = (flags & ZoneFlags.Instanced) === 0; // a dungeon's is never seen again
    const kept = this.storage.read(MapTerrain.cacheName(z.terrainHash));
    if (kept) void this.useTerrain(kept, epoch, false);
    else this.sendInGame({ case: 'mapRequest', value: { epoch } });
  }

  /** Decodes a zone's terrain (kept, or sent by the world) and builds the chunks that were waiting for it. */
  private async useTerrain(data: Uint8Array, epoch: number, sent: boolean): Promise<void> {
    let terrain: MapTerrain | null = null;
    try { terrain = await MapTerrain.decode(data); }
    catch (e) { log.warn(`Ignoring a damaged terrain: ${(e as Error).message}`); }
    if (epoch !== this.world.epoch || this.terrain) return;
    if (!terrain || terrain.hash !== this.terrainHash) {
      if (sent) { log.error("The terrain sent doesn't match its hash"); return; }
      this.sendInGame({ case: 'mapRequest', value: { epoch } }); // the kept copy is damaged: ask for it
      return;
    }
    if (sent && this.cacheTerrain) this.storage.write(MapTerrain.cacheName(terrain.hash), data);
    this.terrain = terrain;
    for (const c of this.pendingChunks) this.applyChunk(c.cx, c.cy, c.changes);
    this.pendingChunks = [];
  }

  /** A chunk coming into view: the terrain's, with what's changed on it since. */
  private applyChunk(cx: number, cy: number, changes: [number, number][]): void {
    const chunk: Chunk = this.terrain!.chunkAt(cx, cy);
    for (const [index, obj] of changes) chunk.changeObject(index, obj);
    this.world.map.set(chunk);
    this.mapMemory.record(chunk);
    this.emit('chunkChanged', cx, cy);
  }

  private handleSnapshot(s: Snapshot): void {
    if (u8(s.epoch) !== this.world.epoch || !this.world.hasZone) return;
    if (this.haveSnapshot && s.tick < this.lastSnapshotTick) return;
    // A crowded view arrives in several parts with the same tick (same self, different entities).
    const newTick = !this.haveSnapshot || s.tick > this.lastSnapshotTick;
    this.haveSnapshot = true;
    this.lastSnapshotTick = s.tick;
    if (newTick) this.clock.onServerTick(s.tick);
    for (const e of s.entities) {
      const ent = this.world.entities.get(u16(e.entityId));
      if (!ent) continue;
      ent.addSample(s.tick, e.x / scale, e.y / scale, facing(e.facingFlags), u8(e.facingFlags >>> 2));
      ent.healthPct = u8(e.healthPct);
    }
    const self = s.self;
    if (self) {
      // Reconciled once per frame with the newest snapshot, after every message is handled.
      this.pendingSelf = {
        lastProcessedInput: s.lastProcessedInput,
        self: {
          x: self.x, y: self.y, facing: facing(self.facingFlags), flags: u8(self.facingFlags >>> 2),
          staminaUnits: int(self.stamina), staminaDelay: u8(self.staminaDelay), stunTicks: u8(self.stun),
        },
      };
    }
  }

  /** Inventory-dependent progress (Collect/Equip), by "quest:objective". */
  private collectProgress(): Map<string, number> {
    const progress = new Map<string, number>();
    for (const id of this.activeQuests.keys()) {
      const def = content().quest(id);
      def?.objectives.forEach((o, i) => { if (o.kind === QuestObjectiveKind.Collect || o.kind === QuestObjectiveKind.Equip) progress.set(`${id}:${i}`, progressOf(o, 0, this.inventory)); });
    }
    return progress;
  }

  /** After inventory changed: report when a Collect/Equip objective lost progress. */
  private noteCollectLoss(before: Map<string, number>): void {
    for (const [key, had] of before) {
      const [quest, index] = key.split(':').map(Number);
      const def = content().quest(quest);
      if (!this.activeQuests.has(quest) || !def) continue;
      const o = def.objectives[index];
      const now = progressOf(o, 0, this.inventory);
      if (now >= had) continue;
      const prev = this.collectLost.get(key);
      const earlier = prev && this.timeNow - prev.at < collectLostSeconds ? prev.lost : 0;
      this.collectLost.set(key, { at: this.timeNow, lost: earlier + had - now });
      this.addNotice(NoticeKind.Error, `${def.title}: ${now}/${o.count} ${content().item(o.target)?.name ?? 'items'} now`);
    }
  }

  private fail(code: DisconnectCode, message: string): void {
    if (this.state === SessionState.Disconnected) return;
    this.state = SessionState.Disconnected;
    this.socket?.close('session ended');
    this.socket = null;
    this.disconnectCode = code;
    this.disconnectMessage = message;
    this.revision++;
    log.info(`Session ended: ${message}`);
    this.emit('disconnected');
  }

  disconnect(): void {
    this.saveMapMemory();
    if (this.state === SessionState.Disconnected) return;
    this.socket?.close('left');
    this.socket = null;
    this.fail(DisconnectCode.Unknown, 'Disconnected.');
  }
}
